import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import { Sync as SyncIcon } from "@mui/icons-material";

import { PageHeader, LabelM, SubtitleM, SubtitleS } from "../../components/Typography";
import useApiCall from "../../hooks/useApiCall";

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export default function AgentInstanceDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const apps = useSelector((state) => state?.apps.list || []);
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();

  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Configuration override editor state
  const [overridesText, setOverridesText] = useState("{}");
  const [overridesValid, setOverridesValid] = useState(true);
  const [overridesError, setOverridesError] = useState(null);

  useEffect(() => {
    fetchInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInstance = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await makeRequest(`/v2/agent_instances/${id}`, {
        method: 'GET',
        authTokens: tokens,
      });
      const instanceData = response.instance || response;
      setInstance(instanceData);

      // Format overrides for display
      const overrides = instanceData.configuration_overrides || {};
      setOverridesText(JSON.stringify(overrides, null, 2));
    } catch (error) {
      console.error("Error fetching instance:", error);
      setError("Failed to load instance details");
    } finally {
      setLoading(false);
    }
  };

  const handleOverridesChange = (value) => {
    setOverridesText(value);
    setOverridesError(null);

    // Validate JSON
    try {
      JSON.parse(value);
      setOverridesValid(true);
    } catch (e) {
      setOverridesValid(false);
      setOverridesError(e.message);
    }
  };

  const handleSaveOverrides = async () => {
    if (!overridesValid) {
      alert("Please fix JSON syntax errors before saving");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const overrides = JSON.parse(overridesText);
      await makeRequest(`/v2/agent_instances/${id}`, {
        method: 'PATCH',
        body: {
          instance: {
            configuration_overrides: overrides,
          },
        },
        authTokens: tokens,
      });

      setSuccessMessage("Configuration overrides saved successfully!");
      await fetchInstance(); // Refresh to get effective config
    } catch (error) {
      console.error("Error saving overrides:", error);
      setError(
        error.response?.data?.error ||
          error.message ||
          "Failed to save overrides"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    if (!instance.template_id) {
      alert("This instance is not linked to a template");
      return;
    }

    if (
      window.confirm(
        `Sync from template?\n\nThis will update this instance with the latest template changes while preserving your configuration overrides.`
      )
    ) {
      setSyncing(true);
      setError(null);
      setSuccessMessage(null);

      try {
        await makeRequest(`/v2/agent_instances/${id}/sync`, {
          method: 'POST',
          authTokens: tokens,
        });
        setSuccessMessage("Instance synced successfully from template!");
        await fetchInstance(); // Refresh data
      } catch (error) {
        console.error("Error syncing instance:", error);
        setError(
          error.response?.data?.error || error.message || "Failed to sync instance"
        );
      } finally {
        setSyncing(false);
      }
    }
  };

  const getClientName = (appId) => {
    const app = apps.find((a) => a.id === appId);
    return app ? app.client_name : `App ID: ${appId}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !instance) {
    return (
      <Box sx={{ my: 4 }}>
        <SubtitleM color="error">{error}</SubtitleM>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate("/agent-instances")}
        >
          Back to Instances
        </Button>
      </Box>
    );
  }

  if (!instance) return null;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <PageHeader>Agent Instance: {instance.name}</PageHeader>
        <Stack direction="row" spacing={2}>
          {instance.template_id && (
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? "Syncing..." : "Sync from Template"}
            </Button>
          )}
          <Button variant="contained" onClick={() => navigate("/agent-instances")}>
            Back to List
          </Button>
        </Stack>
      </Stack>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Basic Info Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <SubtitleM style={{ fontWeight: "bold", marginBottom: "16px" }}>
          Basic Information
        </SubtitleM>
        <Stack direction="column" spacing={1}>
          <SubtitleS>
            <strong>Client:</strong> {getClientName(instance.app_id)}
          </SubtitleS>
          <SubtitleS>
            <strong>Site Code:</strong> {instance.site_code}
          </SubtitleS>
          <SubtitleS>
            <strong>Workflow Type:</strong> {instance.agent_workflow_type || "N/A"}
          </SubtitleS>
          <SubtitleS>
            <strong>Status:</strong> {instance.status || "N/A"}
          </SubtitleS>
          {instance.template_id && (
            <SubtitleS>
              <strong>Template:</strong>{" "}
              <Link
                to={`/agent-templates/${instance.template_id}/edit`}
                style={{ color: "#1976d2", textDecoration: "underline" }}
              >
                View Template
              </Link>
            </SubtitleS>
          )}
        </Stack>
      </Paper>

      {/* Configuration Tabs */}
      <Paper sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Configuration Overrides" />
          <Tab label="Effective Configuration" />
          <Tab label="Template Configuration" />
        </Tabs>

        <Divider sx={{ my: 2 }} />

        {/* Tab 1: Configuration Overrides (Editable) */}
        <TabPanel value={tabValue} index={0}>
          <SubtitleM style={{ marginBottom: "16px" }}>
            Custom configuration overrides for this instance
          </SubtitleM>
          <SubtitleS style={{ marginBottom: "16px", color: "#666" }}>
            These overrides will be merged with the template configuration. Changes here
            will persist even when syncing from the template.
          </SubtitleS>
          <TextField
            multiline
            fullWidth
            minRows={15}
            maxRows={25}
            value={overridesText}
            onChange={(e) => handleOverridesChange(e.target.value)}
            error={!overridesValid}
            helperText={overridesError}
            sx={{
              fontFamily: "monospace",
              "& .MuiInputBase-input": {
                fontFamily: "monospace",
                fontSize: "0.9rem",
              },
            }}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveOverrides}
              disabled={!overridesValid || saving}
            >
              {saving ? <CircularProgress size={24} /> : "Save Overrides"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const formatted = JSON.stringify(
                  JSON.parse(overridesText),
                  null,
                  2
                );
                setOverridesText(formatted);
              }}
              disabled={!overridesValid}
            >
              Format JSON
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                if (
                  window.confirm("Clear all overrides? This cannot be undone.")
                ) {
                  setOverridesText("{}");
                  handleOverridesChange("{}");
                }
              }}
            >
              Clear Overrides
            </Button>
          </Stack>
        </TabPanel>

        {/* Tab 2: Effective Configuration (Read-only) */}
        <TabPanel value={tabValue} index={1}>
          <SubtitleM style={{ marginBottom: "16px" }}>
            Merged configuration (Template + Overrides)
          </SubtitleM>
          <SubtitleS style={{ marginBottom: "16px", color: "#666" }}>
            This is the final configuration that will be used at runtime,
            combining the template with your custom overrides.
          </SubtitleS>
          <TextField
            multiline
            fullWidth
            minRows={15}
            maxRows={25}
            value={JSON.stringify(instance.effective_configuration || {}, null, 2)}
            InputProps={{
              readOnly: true,
            }}
            sx={{
              fontFamily: "monospace",
              "& .MuiInputBase-input": {
                fontFamily: "monospace",
                fontSize: "0.9rem",
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </TabPanel>

        {/* Tab 3: Template Configuration (Read-only) */}
        <TabPanel value={tabValue} index={2}>
          <SubtitleM style={{ marginBottom: "16px" }}>
            Base template configuration
          </SubtitleM>
          <SubtitleS style={{ marginBottom: "16px", color: "#666" }}>
            This is the configuration from the template. It provides the base that
            your overrides are merged with.
          </SubtitleS>
          {instance.template_id ? (
            <TextField
              multiline
              fullWidth
              minRows={15}
              maxRows={25}
              value={JSON.stringify(
                instance.template_configuration || {},
                null,
                2
              )}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                fontFamily: "monospace",
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          ) : (
            <Alert severity="info">
              This instance is not linked to a template.
            </Alert>
          )}
        </TabPanel>
      </Paper>
    </>
  );
}
